/**
 * LeftMenuBoard — 게시판 모듈 사이드바
 *
 * 스코프별 섹션 그룹핑 (Workspace / Channel별)
 * 게시판 유형별 좌측 아이콘 표시
 * 우측 아이콘은 즐겨찾기 토글 전용 (PC: hover 표시, mobile: 상시 표시)
 * 수정 진입은 게시판 내부 설정 버튼(BoardPage / BoardInlineView) 단일 — 좌측 메뉴는 즐겨찾기 전용
 */
import React, { useEffect, useState, useCallback, useMemo } from 'react';
import {
  LayoutList, Bell, FileText, MessageSquare, BookOpen, ListTodo,
  Plus, ChevronDown,
  Hash, Star, Settings,
} from 'lucide-react';
import { Trans, t } from '@lingui/macro';
import { cn } from '../../lib/utils';
import { useAppUIContext } from '../../context/AppUIContext';
import { useBoardContext } from '../../context/BoardContext';
import { useCanManageBoards } from '../../context/WorkspaceSettingsContext';
import { Board, BoardCategory, BoardType, BoardScope } from '../../models/Board';
import { fetchBoards, fetchBoardCategories } from '../../services/BoardService';
import { My } from '../../services/My';
import { MyNotifs } from '../../services/MyNotifs';
import BoardCreateModal from './BoardCreateModal';
import BoardCategoryManageModal from './BoardCategoryManageModal';
import { toast } from '../../components/ui/use-toast';

/** 게시판 유형별 아이콘 */
function boardIcon(type: BoardType) {
  switch (type) {
    case 'notice': return Bell;
    case 'qna': return MessageSquare;
    case 'wiki': return BookOpen;
    case 'issue': return ListTodo;
    default: return FileText;
  }
}

const LeftMenuBoard: React.FC = () => {
  const { activeWorkspaceId, rooms } = useAppUIContext();
  const ctx = useBoardContext();
  // 워크스페이스 정책의 위임(board_manager_user_ids) 도 합쳐서 판단
  const canManage = useCanManageBoards();
  // pinnedIds / togglePin 은 BoardContext 공유 — 대시보드의 즐겨찾기 필터와 실시간 동기화.
  const { boards, setBoards, selectedBoard, setSelectedBoard, refreshBoards, pinnedIds, togglePin, loading } = ctx;

  const [showBoardCreate, setShowBoardCreate] = useState(false);
  const [createScope, setCreateScope] = useState<BoardScope>('workspace');
  const [createChannelId, setCreateChannelId] = useState<string | undefined>(undefined);
  const [unreadBoardIds, setUnreadBoardIds] = useState<Set<string>>(new Set());
  // 카테고리
  const [categories, setCategories] = useState<BoardCategory[]>([]);
  const [showCategoryManage, setShowCategoryManage] = useState(false);
  const refreshCategories = useCallback(() => {
    if (!activeWorkspaceId) return;
    fetchBoardCategories(activeWorkspaceId).then(setCategories).catch(() => {});
  }, [activeWorkspaceId]);
  // 카테고리 collapse 지속 (localStorage). key = `boardCategoriesCollapsed:{wsId}`
  const COLLAPSE_KEY = activeWorkspaceId ? `boardCategoriesCollapsed:${activeWorkspaceId}` : null;
  const [collapsedCats, setCollapsedCats] = useState<Set<string>>(() => {
    if (typeof window === 'undefined') return new Set();
    try {
      const raw = localStorage.getItem(`boardCategoriesCollapsed:${activeWorkspaceId ?? ''}`);
      return raw ? new Set(JSON.parse(raw) as string[]) : new Set();
    } catch { return new Set(); }
  });
  const toggleCollapse = (catId: string) => {
    setCollapsedCats(prev => {
      const next = new Set(prev);
      if (next.has(catId)) next.delete(catId); else next.add(catId);
      if (COLLAPSE_KEY) {
        try { localStorage.setItem(COLLAPSE_KEY, JSON.stringify(Array.from(next))); } catch { /* ignore */ }
      }
      return next;
    });
  };
  // 워크스페이스 전환 시 collapse 상태 리로드
  useEffect(() => {
    if (!activeWorkspaceId) return;
    try {
      const raw = localStorage.getItem(`boardCategoriesCollapsed:${activeWorkspaceId}`);
      setCollapsedCats(raw ? new Set(JSON.parse(raw) as string[]) : new Set());
    } catch { setCollapsedCats(new Set()); }
  }, [activeWorkspaceId]);

  // 채널 ID → 채널명 매핑
  const channelNameMap = useMemo(() => {
    const map = new Map<string, string>();
    rooms.forEach(r => map.set(r.id, r.name));
    return map;
  }, [rooms]);

  // 워크스페이스 변경 시 게시판 목록 로드 + 미읽음 체크 + 고정 목록.
  // 보드 자동 선택 로직은 BoardProvider 의 URL→state sync 에서 처리 — URL boardId 가 있으면 그쪽 우선,
  // URL hub 일 때만 첫 보드 fallback. 여기서는 보드 목록만 로드.
  useEffect(() => {
    if (!activeWorkspaceId) return;
    fetchBoards(activeWorkspaceId)
      .then((list) => {
        setBoards(list);
      })
      .catch(err => console.error('[LeftMenuBoard] 게시판 목록 로드 실패:', err));

    // pinnedIds 는 BoardProvider 에서 fetch — 여기서는 카테고리만 추가 로드.
    refreshCategories();
  }, [activeWorkspaceId]);

  // 미읽음 배지 — 통지센터 알림(isNew) 기준으로 통일 (채널 탭바/모듈 rail 과 동일). 실시간 구독.
  useEffect(() => {
    const compute = () => setUnreadBoardIds(new Set(
      MyNotifs.Notifications.filter(n => n.isNew && n.source === 'board' && n.boardId).map(n => n.boardId!),
    ));
    compute();
    return MyNotifs.subscribeToNotificationUpdates(compute);
  }, []);

  const handleSelectBoard = useCallback((board: Board) => {
    setSelectedBoard(board);
  }, [setSelectedBoard]);

  const handleTogglePin = useCallback(async (boardId: string) => {
    const isPinned = await togglePin(boardId);
    toast({ title: isPinned ? t`Added to favorites` : t`Removed from favorites` });
  }, [togglePin]);

  const handleCreateForScope = (scope: BoardScope, channelId?: string) => {
    setCreateScope(scope);
    setCreateChannelId(channelId);
    setShowBoardCreate(true);
  };

  // 스코프별 그룹핑
  const pinnedSet = useMemo(() => new Set(pinnedIds), [pinnedIds]);
  const workspaceBoards = boards.filter(b => b.scope === 'workspace' || !b.scope);
  const channelBoards = boards.filter(b => b.scope === 'channel');

  // 고정 게시판 (모든 스코프에서)
  const pinnedBoards = useMemo(() => {
    return pinnedIds
      .map(id => boards.find(b => b.id === id))
      .filter((b): b is Board => !!b);
  }, [pinnedIds, boards]);

  // 채널별 서브그룹핑
  const channelGroups = useMemo(() => {
    const groups = new Map<string, Board[]>();
    channelBoards.forEach(b => {
      const cid = b.channel_id ?? 'unknown';
      if (!groups.has(cid)) groups.set(cid, []);
      groups.get(cid)!.push(b);
    });
    return groups;
  }, [channelBoards]);

  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto px-4 pt-0">
        {/* 헤더 — 모듈 이름(단순 라벨) + 카테고리 관리 + 새 게시판.
            대시보드 진입은 LeftMenuHeader 의 모듈 스위처에서 게시판 탭 클릭 시 자동.
            ※ 통합 알림 설정은 대시보드 헤더로 이동 — 게시판 관리 액션과 분리해 사용자 개인 설정임을 명확히 함
            폰트/여백은 채널 사이드바(LeftMenuRooms getMenuTitle)와 통일 — text-label / font-bold / uppercase */}
        <div className="flex items-center gap-1 mb-4">
          <span className="flex-1 text-label font-bold text-muted-foreground uppercase">
            <Trans>Boards</Trans>
          </span>
          {canManage && (
            <>
              <button
                type="button"
                onClick={() => setShowCategoryManage(true)}
                className="size-7 rounded-md flex items-center justify-center text-admin/70 hover:text-admin hover:bg-admin/10 transition-colors cursor-pointer"
                title={t`Manage categories`}
                aria-label={t`Manage categories`}
              >
                <Settings className="size-3.5" />
              </button>
              <button
                type="button"
                onClick={() => handleCreateForScope('workspace')}
                className="size-7 rounded-md flex items-center justify-center text-admin/70 hover:text-admin hover:bg-admin/10 transition-colors cursor-pointer"
                title={t`Create new board`}
                aria-label={t`Create new board`}
              >
                <Plus className="size-3.5" />
              </button>
            </>
          )}
        </div>

        {/* ── ★ 즐겨찾기 (상단 그룹, 아래쪽 워크스페이스 목록과 이중 표시) ── */}
        {pinnedBoards.length > 0 && (
          <BoardSection
            label={t`★ Favorites`}
            boards={pinnedBoards}
            selectedBoard={selectedBoard}
            onSelect={handleSelectBoard}
            unreadBoardIds={unreadBoardIds}
            pinnedIds={pinnedSet}
            onTogglePin={handleTogglePin}
          />
        )}

        {/* ── 워크스페이스 게시판 ──
            카테고리 0개 → 평면 list. ★ 즐겨찾기 가 있을 때만 '전체' 라벨로 시각 구분 부여
            카테고리 1+ → 그룹별 collapsible + 미지정 (보드가 있을 때만) */}
        {categories.length === 0 ? (
          <BoardSection
            label={pinnedBoards.length > 0 ? t`All` : undefined}
            boards={workspaceBoards}
            selectedBoard={selectedBoard}
            onSelect={handleSelectBoard}
            unreadBoardIds={unreadBoardIds}
            pinnedIds={pinnedSet}
            onTogglePin={handleTogglePin}
          />
        ) : (
          (() => {
            const byCat = new Map<string, Board[]>();
            const unassigned: Board[] = [];
            workspaceBoards.forEach(b => {
              if (b.category_id && categories.some(c => c.id === b.category_id)) {
                const list = byCat.get(b.category_id) ?? [];
                list.push(b);
                byCat.set(b.category_id, list);
              } else {
                unassigned.push(b);
              }
            });
            // 각 카테고리 내 boards 는 category_sort_order asc, then created_at asc
            const sortBoards = (list: Board[]) =>
              [...list].sort((a, b) =>
                (a.category_sort_order - b.category_sort_order) ||
                a.created_at.localeCompare(b.created_at)
              );
            return (
              <>
                {categories.map(cat => {
                  const list = sortBoards(byCat.get(cat.id) ?? []);
                  const isCollapsed = collapsedCats.has(cat.id);
                  return (
                    <div key={cat.id} className="space-y-0.5">
                      <button
                        type="button"
                        onClick={() => toggleCollapse(cat.id)}
                        className="w-full flex items-center justify-between px-2 pt-2 pb-1 group"
                      >
                        <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
                          <ChevronDown className={cn('size-3 transition-transform duration-200', isCollapsed && '-rotate-90')} />
                          <span className="truncate">{cat.name}</span>
                          <span className="text-meta-sm font-normal ml-0.5 opacity-60">{list.length}</span>
                        </span>
                      </button>
                      {!isCollapsed && list.map(board => (
                        <BoardItem
                          key={board.id}
                          board={board}
                          isActive={selectedBoard?.id === board.id}
                          onSelect={handleSelectBoard}
                          hasUnread={unreadBoardIds.has(board.id)}
                          isPinned={pinnedSet.has(board.id)}
                          onTogglePin={handleTogglePin}
                        />
                      ))}
                    </div>
                  );
                })}
                {/* 미지정 — 보드가 0개면 hidden (Q1-c) */}
                {unassigned.length > 0 && (() => {
                  const list = sortBoards(unassigned);
                  const UNASSIGNED_KEY = '__unassigned__';
                  const isCollapsed = collapsedCats.has(UNASSIGNED_KEY);
                  return (
                    <div className="space-y-0.5">
                      <button
                        type="button"
                        onClick={() => toggleCollapse(UNASSIGNED_KEY)}
                        className="w-full flex items-center justify-between px-2 pt-2 pb-1 group"
                      >
                        <span className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider group-hover:text-foreground transition-colors">
                          <ChevronDown className={cn('size-3 transition-transform duration-200', isCollapsed && '-rotate-90')} />
                          <span><Trans>Uncategorized</Trans></span>
                          <span className="text-meta-sm font-normal ml-0.5 opacity-60">{list.length}</span>
                        </span>
                      </button>
                      {!isCollapsed && list.map(board => (
                        <BoardItem
                          key={board.id}
                          board={board}
                          isActive={selectedBoard?.id === board.id}
                          onSelect={handleSelectBoard}
                          hasUnread={unreadBoardIds.has(board.id)}
                          isPinned={pinnedSet.has(board.id)}
                          onTogglePin={handleTogglePin}
                        />
                      ))}
                    </div>
                  );
                })()}
              </>
            );
          })()
        )}

        {/* ── 채널 게시판 (채널별 그룹핑) — 비표시.
            게시판 모듈 사이드바는 워크스페이스 게시판 중심. 채널 게시판은 채널 사이드바(BoardInlineView)
            에서 접근. channelGroups/ChannelBoardGroup state·logic 은 향후 admin/owner view 부활 위해 보존. */}
        {false && channelGroups.size > 0 && (
          <div className="mt-4 space-y-1">
            <div className="flex items-center justify-between px-2 pt-2 pb-1">
              <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                <Trans>Channel</Trans>
              </span>
            </div>
            {Array.from(channelGroups.entries()).map(([channelId, chBoards]) => {
              const channelName = channelNameMap.get(channelId) ?? channelId.slice(0, 8);
              return (
                <ChannelBoardGroup
                  key={channelId}
                  channelId={channelId}
                  channelName={channelName}
                  boards={chBoards}
                  selectedBoard={selectedBoard}
                  onSelect={handleSelectBoard}
                  onCreateClick={canManage ? () => handleCreateForScope('channel', channelId) : undefined}
                  canManage={canManage}
                  unreadBoardIds={unreadBoardIds}
                  pinnedIds={pinnedSet}
                  onTogglePin={handleTogglePin}
                />
              );
            })}
          </div>
        )}

        {/* 빈 상태 — 로딩 중엔 숨김(채널처럼 prefetch 되므로 보통 깜빡임 없음). 로딩 끝 + 0개일 때만 */}
        {!loading && boards.length === 0 && (
          <div className="px-2 py-8 text-center">
            <div className="size-12 rounded-2xl bg-primary/8 flex items-center justify-center mx-auto mb-3">
              <LayoutList className="size-5 text-primary" strokeWidth={1.5} />
            </div>
            <p className="text-xs text-muted-foreground mb-2"><Trans>No boards yet</Trans></p>
            {/* 첫 게시판 생성은 관리 권한(canManage) 있을 때만 — 비관리자에게 생성 버튼/모달 노출 금지 */}
            {canManage && (
              <button
                onClick={() => handleCreateForScope('workspace')}
                className="text-xs text-primary font-medium hover:underline cursor-pointer"
              >
                <Trans>Create first board</Trans>
              </button>
            )}
          </div>
        )}
      </div>

      <BoardCreateModal
        open={showBoardCreate}
        onOpenChange={setShowBoardCreate}
        initialScope={createScope}
        initialChannelId={createChannelId}
      />

      {/* 게시판 수정 진입점은 게시판 내부 설정 버튼(BoardPage / BoardInlineView) 단일 — 좌측 메뉴는 즐겨찾기 전용. */}

      {/* 카테고리 관리 모달 — canManage 시에만 진입 가능. 변경 시 카테고리 + 보드 목록 모두 refresh */}
      <BoardCategoryManageModal
        open={showCategoryManage}
        onClose={() => setShowCategoryManage(false)}
        workspaceId={activeWorkspaceId}
        onChanged={() => { refreshCategories(); refreshBoards(); }}
      />
    </div>
  );
};

// ── 채널별 보드 그룹 서브컴포넌트 ──────────────────────────────────

interface ChannelBoardGroupProps {
  channelId: string;
  channelName: string;
  boards: Board[];
  selectedBoard: Board | null;
  onSelect: (board: Board) => void;
  onCreateClick?: () => void;
  canManage?: boolean;
  unreadBoardIds?: Set<string>;
  pinnedIds?: Set<string>;
  onTogglePin?: (id: string) => void;
}

const ChannelBoardGroup: React.FC<ChannelBoardGroupProps> = ({
  channelName, boards, selectedBoard,
  onSelect, onCreateClick, canManage, unreadBoardIds,
  pinnedIds, onTogglePin,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className="ml-1">
      {/* 채널명 헤더 */}
      <div className="flex items-center justify-between px-2 py-1">
        <button
          onClick={() => setCollapsed(!collapsed)}
          className="flex items-center gap-1.5 text-meta-sm font-medium text-muted-foreground cursor-pointer hover:text-foreground transition-colors"
        >
          <ChevronDown className={cn('size-3 transition-transform duration-200', collapsed && '-rotate-90')} />
          <Hash className="size-3 opacity-60" />
          <span className="truncate max-w-[120px]">{channelName}</span>
          <span className="text-meta-sm opacity-50 ml-0.5">{boards.length}</span>
        </button>
        {canManage && (
          <button
            onClick={onCreateClick}
            className="text-admin/70 hover:text-admin transition-colors cursor-pointer p-0.5 rounded hover:bg-admin/10 opacity-0 group-hover:opacity-100"
            title={t`Create channel board`}
          >
            <Plus className="size-3" />
          </button>
        )}
      </div>

      {/* 보드 목록 */}
      {!collapsed && (
        <div className="space-y-0.5 ml-2">
          {boards.map(board => (
            <BoardItem
              key={board.id}
              board={board}
              isActive={selectedBoard?.id === board.id}
              onSelect={onSelect}
              hasUnread={unreadBoardIds?.has(board.id) ?? false}
              isPinned={pinnedIds?.has(board.id) ?? false}
              onTogglePin={onTogglePin}
            />
          ))}
        </div>
      )}
    </div>
  );
};

// ── 워크스페이스 섹션 서브컴포넌트 ─────────────────────────────────────────

interface BoardSectionProps {
  /** 섹션 헤더 라벨. 미제공 시 헤더 없이 목록만 렌더 */
  label?: string;
  boards: Board[];
  selectedBoard: Board | null;
  onSelect: (board: Board) => void;
  onCreateClick?: () => void;
  className?: string;
  unreadBoardIds?: Set<string>;
  pinnedIds?: Set<string>;
  onTogglePin?: (id: string) => void;
}

const BoardSection: React.FC<BoardSectionProps> = ({
  label, boards, selectedBoard,
  onSelect, onCreateClick, className, unreadBoardIds,
  pinnedIds, onTogglePin,
}) => {
  const [collapsed, setCollapsed] = useState(false);

  return (
    <div className={cn('space-y-0.5', className)}>
      {/* 섹션 헤더 — label 있을 때만 */}
      {label && (
        <div className="flex items-center justify-between px-2 pt-2 pb-1">
          <button
            onClick={() => setCollapsed(!collapsed)}
            className="flex items-center gap-1 text-xs font-semibold text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground transition-colors"
          >
            <ChevronDown className={cn('size-3 transition-transform duration-200', collapsed && '-rotate-90')} />
            <span>{label}</span>
            <span className="text-meta-sm font-normal ml-0.5 opacity-60">{boards.length}</span>
          </button>
          {onCreateClick && (
            <button
              onClick={onCreateClick}
              className="text-admin/70 hover:text-admin transition-colors cursor-pointer p-0.5 rounded hover:bg-admin/10"
              title={t`Create board`}
            >
              <Plus className="size-3.5" />
            </button>
          )}
        </div>
      )}

      {/* 게시판 목록 */}
      {!collapsed && boards.map(board => (
        <BoardItem
          key={board.id}
          board={board}
          isActive={selectedBoard?.id === board.id}
          onSelect={onSelect}
          hasUnread={unreadBoardIds?.has(board.id) ?? false}
          isPinned={pinnedIds?.has(board.id) ?? false}
          onTogglePin={onTogglePin}
        />
      ))}
    </div>
  );
};

// ── 개별 보드 아이템 서브컴포넌트 ──────────────────────────────────────────

interface BoardItemProps {
  board: Board;
  isActive: boolean;
  onSelect: (board: Board) => void;
  hasUnread?: boolean;
  isPinned?: boolean;
  onTogglePin?: (id: string) => void;
}

const BoardItem: React.FC<BoardItemProps> = ({
  board, isActive, onSelect, hasUnread, isPinned, onTogglePin,
}) => {
  const Icon = boardIcon(board.type as BoardType);

  return (
    <div className="relative group">
      <button
        onClick={() => onSelect(board)}
        className={cn(
          'w-full flex items-center gap-2.5 pl-2.5 pr-9 py-2 rounded-lg text-label transition-all duration-150 cursor-pointer',
          isActive
            ? 'bg-primary/10 text-primary font-medium'
            : 'text-foreground/70 hover:bg-muted/60 hover:text-foreground',
        )}
      >
        {/* 유형 아이콘 — 공지/Q&A/위키/이슈 등 */}
        <Icon className={cn('size-4 shrink-0', isActive ? 'text-primary' : 'text-muted-foreground')} />
        <span className="truncate flex-1 text-left">{board.name}</span>

        {/* 미읽음 배지 */}
        {hasUnread && !isActive && (
          <span className="size-2 rounded-full bg-primary shrink-0 animate-in fade-in duration-300" />
        )}
      </button>

      {/* 즐겨찾기 토글
          - 핀 상태(isPinned): 항상 노출 (어디서든 즐겨찾기 보이도록)
          - 미핀 + 모바일(hover 미지원): 상시 노출
          - 미핀 + 데스크탑(md+, hover 가능): 행 hover 시에만 노출
          수정은 게시판 내부 설정 버튼에서 처리 — 좌측 메뉴는 즐겨찾기 전용 */}
      {onTogglePin && (
        <button
          onClick={(e) => { e.stopPropagation(); onTogglePin(board.id); }}
          className={cn(
            'absolute right-1 top-1/2 -translate-y-1/2 size-6 rounded-md flex items-center justify-center transition-opacity cursor-pointer hover:bg-muted',
            isPinned
              ? 'opacity-100 text-amber-400'
              : 'opacity-100 md:opacity-0 md:group-hover:opacity-100 text-muted-foreground/70 hover:text-foreground',
          )}
          title={isPinned ? t`Remove from favorites` : t`Add to favorites`}
          aria-label={isPinned ? t`Remove from favorites` : t`Add to favorites`}
        >
          <Star className={cn('size-3.5', isPinned && 'fill-amber-400')} />
        </button>
      )}
    </div>
  );
};

export default LeftMenuBoard;
